import { PRINTED_CATALOGS } from "../../api/config"
import { describeError } from "../../api/http"
import { TranslateSource, type Catalog, type TranslateRow } from "../../api/types"

/** Правила справочника переводов, вынесенные из разметки: как считаются
 *  символы, что бэк разносит по одинаковым фразам, какие разделы он умеет
 *  применять и какие каталоги показываем в фильтре. */

/** Фраз в одном запросе к переводчику. Бэк дробит и сам — по 49 значений и
 *  128 КБ на запрос к DeepL (`DeeplTranslatorService.cs:105-112`), — но дробит
 *  внутри одного HTTP-запроса: на тысячах фраз ответа никто не дождётся.
 *  Поэтому пачками ходим сами — виден прогресс и работает отмена. */
export const TRANSLATE_CHUNK = 100

/** Строк в одном запросе на сохранение. Столько же берёт партиция внутри
 *  `TranslateEditor` (`maxBatchSize = 200`), и вся пачка идёт одной транзакцией. */
export const SAVE_CHUNK = 200

/** С какого числа фраз спрашиваем перед машинным переводом: символы уходят
 *  в квоту DeepL, и лишний клик там дешевле лишней тысячи фраз. */
export const CONFIRM_FROM = 25

/** Поля, которые лежат не в `Translates`, а в json-полях атрибута. */
export const SHORT_NAME_KEY = "AttributeShortName"
export const UNIT_KEY = "AttributeUnit"

/** Разделы, для которых бэк умеет отдать ссылку на товар на сайте
 *  (`TranslateReader.cs:109-115`). У остальных ссылки не будет никогда. */
export const LINKABLE = new Set<TranslateSource>([
	TranslateSource.GroupName,
	TranslateSource.ValueDescription,
	TranslateSource.PictGroupDesc,
])

/** Порядок — по тому, чего в наборе больше и что чаще правят. */
export const SOURCES: { value: TranslateSource; label: string }[] = [
	{ value: TranslateSource.All, label: "Все разделы" },
	{ value: TranslateSource.GroupName, label: "Названия групп" },
	{ value: TranslateSource.PictGroupDesc, label: "Описания групп" },
	{ value: TranslateSource.AttrNames, label: "Названия атрибутов" },
	{ value: TranslateSource.ValueDescription, label: "Описания значений" },
	{ value: TranslateSource.CatWeb, label: "Категории сайта" },
	{ value: TranslateSource.CatKat, label: "Категории печатного каталога" },
	{ value: TranslateSource.PictGroup, label: "Пиктограммы" },
]

export const SOURCE_NAME = new Map(SOURCES.map(item => [item.value, item.label]))

/**
 * Разделы, которые умеет разложить по таблицам «Применить переводы»
 * (`update-sources-from-translates`). Список закрытый: остальное бэк не
 * применяет вовсе — см. `translateWrites.applyToSources`.
 */
export const APPLIABLE: {
	value: TranslateSource
	label: string
	hint: string
	risky?: boolean
}[] = [
	{
		value: TranslateSource.AttrNames,
		label: "Названия атрибутов",
		hint: "перевод ляжет в Attributes.NameJson",
	},
	{
		value: TranslateSource.CatWeb,
		label: "Категории сайта",
		hint: "перевод ляжет в WebCategories.NameJson",
	},
	{
		value: TranslateSource.CatKat,
		label: "Категории печатного каталога",
		hint: "перевод ляжет в CatalogCategories.NameJson",
	},
	{
		value: TranslateSource.PictGroup,
		label: "Пиктограммы",
		hint: "перевод ляжет в Pictograms.NameJson",
	},
	{
		value: TranslateSource.PictGroupDesc,
		label: "Описания и названия групп",
		hint:
			"описание собирается заново из справочника: пункты без перевода выпадут, " +
			"а у групп без переводов описание на этом языке станет пустым",
		risky: true,
	},
]

/** Разделы, применяемые по умолчанию: описания групп выключены — эта ветка
 *  перезаписывает описание целиком и способна его очистить. */
export const SAFE_APPLIABLE = [
	TranslateSource.AttrNames,
	TranslateSource.CatWeb,
	TranslateSource.CatKat,
	TranslateSource.PictGroup,
]

/** Каталоги для фильтра: сайты Риги (`WebSites.Schema = 2`, имена каталогов и
 *  сайтов совпадают) и печатные из списка `PRINTED_CATALOGS`.
 *
 *  Каталога `www.autoinstrumenti.lv` в таблице `WebSites` нет — значит и в
 *  фильтр он не попадает. Если он Риге нужен, вписать его в тот список. */
export function splitCatalogs(catalogs: Catalog[], rigaSites: Set<string>) {
	const riga: Catalog[] = []
	const printed: Catalog[] = []
	for (const catalog of catalogs) {
		if (catalog.isProductCatalog) continue
		if (catalog.isPrinted) {
			if (PRINTED_CATALOGS.includes(catalog.name)) printed.push(catalog)
		} else if (rigaSites.has(catalog.name)) riga.push(catalog)
	}
	// порядок из PRINTED_CATALOGS, а не из ответа ERP
	printed.sort(
		(a, b) =>
			PRINTED_CATALOGS.indexOf(a.name) - PRINTED_CATALOGS.indexOf(b.name)
	)
	return { riga, printed }
}

/** Ключ разноса: бэк ищет одинаковые русские фразы внутри одного раздела. */
export function sameTextKey(row: TranslateRow): string | null {
	const russian = row.russian?.trim()
	if (!russian) return null // пустой русский бэк не разносит
	return row.source + "|" + russian.toLowerCase()
}

/** Символы без пробелов — ровно так их считает бэк (`TranslateReader.cs:117`)
 *  и так же их считают переводчикам и в квоте DeepL. */
export function symbols(text: string | null | undefined): number {
	return text ? text.replace(/\s/g, "").length : 0
}

/**
 * Обрезка перевода ровно так, как это делает бэк перед записью
 * (`TranslateTrimmer.TrimTranslate`). Повторяем на клиенте, чтобы после
 * сохранения в поле стояло то же, что легло в базу, и строка не оставалась
 * «изменённой» из-за лишней точки.
 */
export function trimTranslate(value: string): string {
	let result = value
	if (result.startsWith("•") && result.length > 2 && result[1] !== " ") {
		result = result[0] + " " + result.slice(1)
	}
	// именно пробел, точка и запятая — перевод строки бэк не трогает
	return result.replace(/^[ .,]+|[ .,]+$/g, "")
}

/** Ячейка CSV: точку с запятой, кавычки и переносы строк Excel понимает
 *  только в кавычках, а сами кавычки внутри — удвоенными. */
export function escapeCsv(value: string): string {
	return /[;"\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

/** Ссылки бэк собирает из базы самого сайта (`UrlGroupProvider`), и это
 *  единственная выборка справочника, которая ходит в MySQL OpenCart. На
 *  локальном стенде тех баз нет — ecm отвечает `500` и голым текстом
 *  «Unable to connect to any of the specified MySQL hosts». Переводим. */
export function describeUrlsError(error: unknown): string {
	const text = describeError(error)
	return /mysql/i.test(text)
		? "база сайта недоступна — на локальном стенде так и должно быть"
		: text
}
